import React, { useState } from "react";
import { ArrowRight, ArrowLeft, Check, RotateCcw } from "lucide-react";
import { Reveal } from "./Reveal";
import {
  GOVERNED_STEPS, PROOF_ITEMS, CUSTODIANS, AUTHORITY_ROLE,
  DECISIONS, CAR, SCENARIO_REF,
} from "./data";

export default function GovernedJourney() {
  const [step, setStep] = useState(1);
  const [decision, setDecision] = useState(null);

  const total = GOVERNED_STEPS.length;
  const choose = (key) => { setDecision(key); setStep(7); };
  const restart = () => { setDecision(null); setStep(1); };

  const dec = decision ? DECISIONS[decision] : null;

  return (
    <section className="section section--gov" id="governed">
      <div className="wrap">
        <Reveal>
          <div className="sec-head">
            <span className="sec-kicker sec-kicker--blue">The KATLAS journey</span>
            <h2 className="sec-title">Request the decision, not the data.</h2>
            <p className="sec-sub">
              The same 09:12 event — now on a governed path. Move through each step.
            </p>
          </div>
        </Reveal>

        <div className="gov" data-testid="governed-journey">
          {/* progress rail */}
          <ol className="gov__rail" aria-label="Governed steps">
            {GOVERNED_STEPS.map((s) => {
              const state = step > s.n ? "done" : step === s.n ? "now" : "todo";
              return (
                <li key={s.n} className={"gov__step gov__step--" + state}
                    data-testid={"gov-step-" + s.n}
                    onClick={() => { if (s.n <= step) setStep(s.n); }}>
                  <span className="gov__num">{state === "done" ? <Check size={13} /> : s.n}</span>
                  <span className="gov__label">{s.label}</span>
                </li>
              );
            })}
          </ol>

          {/* active panel */}
          <div className={"gov__panel" + (dec ? " gov__panel--" + dec.tone : "")} data-testid="gov-panel">
            <div className="gov__panelhead">
              <span className="gov__panelno">STEP {step} / {total}</span>
              <span className="gov__paneltitle">{GOVERNED_STEPS[step - 1].label}</span>
            </div>

            {step === 1 && (
              <div className="gov__content">
                <p className="gov__lead">A bounded request is opened — narrow, specific, and time-bound.</p>
                <div className="req-chip" data-testid="gov-request-chip">
                  <span className="req-chip__k">REQUEST</span>
                  <span className="req-chip__v">Conditional release decision</span>
                  <span className="req-chip__ref">REF {SCENARIO_REF}</span>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="gov__content">
                <p className="gov__lead">The purpose is stated up front, so the request cannot quietly widen.</p>
                <div className="purpose">“Determine whether the consignment may proceed.”</div>
              </div>
            )}

            {step === 3 && (
              <div className="gov__content">
                <p className="gov__lead">Only the minimum proof needed to decide — not the underlying documents.</p>
                <ul className="proof">
                  {PROOF_ITEMS.map((p) => (
                    <li key={p.id} className="proof__item" data-testid={"proof-" + p.id}>
                      <span className="proof__check"><Check size={13} /></span>
                      <span className="proof__label">{p.label}</span>
                      <span className="proof__holder">held by {p.holder}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {step === 4 && (
              <div className="gov__content">
                <p className="gov__lead">The request is routed to the role entitled to decide — not broadcast.</p>
                <div className="route">
                  <span className="route__from">REQUEST</span>
                  <ArrowRight size={18} className="route__arrow" />
                  <span className="route__to" data-testid="gov-authority-role">{AUTHORITY_ROLE}</span>
                </div>
              </div>
            )}

            {step === 5 && (
              <div className="gov__content">
                <p className="gov__lead">Evidence never centralises. It stays with its custodian; only a confirmation crosses.</p>
                <ul className="custody">
                  {CUSTODIANS.map((c, i) => (
                    <li key={i} className="custody__item" data-testid={"custody-" + i}>
                      <div className="custody__top">
                        <span className="custody__holder">{c.holder}</span>
                        <span className="custody__badge">stays with owner</span>
                      </div>
                      <div className="custody__row">
                        <span className="custody__holds">{c.holds}</span>
                        <span className="custody__shared">{c.shared}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {step === 6 && (
              <div className="gov__content">
                <p className="gov__lead">The entitled role makes an accountable decision.</p>
                <div className="decide" data-testid="gov-decisions">
                  {Object.entries(DECISIONS).map(([key, d]) => (
                    <button key={key}
                      className={"decide__btn decide__btn--" + d.tone}
                      data-testid={"decision-" + key}
                      onClick={() => choose(key)}>
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 7 && dec && (
              <div className="gov__content">
                <div className={"receipt receipt--" + dec.tone} data-testid="gov-receipt">
                  <div className="receipt__head">
                    <span className="receipt__title">Outcome receipt</span>
                    <span className="receipt__ref">REF {SCENARIO_REF}</span>
                  </div>
                  <dl className="receipt__grid">
                    <div><dt>Request</dt><dd>Conditional release decision</dd></div>
                    <div><dt>Purpose</dt><dd>Determine whether the consignment may proceed</dd></div>
                    <div><dt>Proof referenced</dt><dd>Inspection validity · Insurance condition · Seal-status exception</dd></div>
                    <div><dt>Authority</dt><dd>{AUTHORITY_ROLE}</dd></div>
                    <div className="receipt__outcome"><dt>Decision</dt><dd>{dec.outcome}</dd></div>
                    <div><dt>Resolved</dt><dd>09:13 · evidence retained by each holder</dd></div>
                  </dl>
                  <p className="receipt__illus" data-testid="receipt-illustrative">
                    Illustrative governed outcome — not a live KATLAS-signed receipt.
                  </p>
                </div>

                <div className="car" data-testid="car-legend">
                  {CAR.map((c) => (
                    <div key={c.k} className="car__item">
                      <span className="car__k">{c.k}</span>
                      <span className="car__v">{c.v}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* controls */}
            <div className="gov__nav">
              {step > 1 && step !== 7 && (
                <button className="btn btn--ghost btn--sm" data-testid="gov-back"
                        onClick={() => setStep((s) => s - 1)}>
                  <ArrowLeft size={15} /> Back
                </button>
              )}
              {step < 6 && (
                <button className="btn btn--accent btn--sm" data-testid="gov-next"
                        onClick={() => setStep((s) => s + 1)}>
                  Next <ArrowRight size={15} />
                </button>
              )}
              {step === 7 && (
                <button className="btn btn--ghost btn--sm" data-testid="gov-restart"
                        onClick={restart}>
                  <RotateCcw size={15} /> Run another decision
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
